import React, { useEffect, useState } from 'react';
import { fetchIncomingJobs, receivePortalJob } from '../api';
import './incomingjobs.css';

// Jobs customers sent in on the commercial portal that are still waiting for their
// wheels: pickups the driver hasn't made yet and drop-offs that haven't arrived. They
// stay here, above the job list, until someone taps Picked up / Received.
//
// This section has its own filters, separate from the job list's: someone with a
// default contact starts on their own pickups (plus drop-offs and pickups nobody is
// driving, which aren't anyone's in particular) and can switch to all; anyone can
// narrow it to pickups or drop-offs.

const dayLabel = (isoDate) => {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const sentLabel = (iso) => new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

export const intakeLabel = (job) => {
  if (job.intake_method === 'dropoff') return 'Drop-off';
  if (job.intake_method !== 'pickup') return '';
  return job.pickup_asap || !job.pickup_date ? 'Pickup ASAP' : `Pickup ${dayLabel(job.pickup_date)}`;
};

const sameContact = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

const TYPES = [
  { value: 'all', label: 'All' },
  { value: 'pickup', label: 'Pickups' },
  { value: 'dropoff', label: 'Drop-offs' },
];

function Toggle({ options, value, onChange, label }) {
  return (
    <div className="incoming-filter" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" aria-pressed={value === option.value}
                className={`incoming-filter-option ${value === option.value ? 'active' : ''}`}
                onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

function IncomingJobs({ defaultContact, search, ready, refreshKey, onOpen, onReceived }) {
  const [jobs, setJobs] = useState([]);
  const [whose, setWhose] = useState('mine');
  const [type, setType] = useState('all');
  const [open, setOpen] = useState(false); // closed when the job list opens; the count shows what's waiting
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    fetchIncomingJobs([], search)
      .then((data) => { if (!cancelled) { setJobs(data.results || []); setError(''); } })
      .catch(() => { if (!cancelled) setError('Could not load incoming jobs.'); });
    return () => { cancelled = true; };
  }, [ready, search, refreshKey, reload]);

  // A job marked received from its own window (PortalJobBanner) leaves this list too.
  useEffect(() => {
    const onReceivedElsewhere = (event) => {
      setJobs((list) => list.filter((j) => j.id !== event.detail.id));
      if (onReceived) onReceived(event.detail);
    };
    window.addEventListener('portal-job-received', onReceivedElsewhere);
    return () => window.removeEventListener('portal-job-received', onReceivedElsewhere);
  }, [onReceived]);

  const receive = async (event, job) => {
    event.stopPropagation();
    setBusyId(job.id);
    try {
      const updated = await receivePortalJob(job.id);
      setJobs((list) => list.filter((j) => j.id !== job.id));
      if (onReceived) onReceived(updated);
    } catch {
      setError('Could not mark that job received. Try again.');
      setReload((n) => n + 1);
    } finally {
      setBusyId(null);
    }
  };

  if (!jobs.length && !error) return null;

  const onlyMine = Boolean(defaultContact) && whose === 'mine';
  const shown = jobs.filter((job) => {
    const pickup = job.intake_method === 'pickup';
    if (type === 'pickup' && !pickup) return false;
    if (type === 'dropoff' && job.intake_method !== 'dropoff') return false;
    return !onlyMine || !pickup || !(job.contact || '').trim() || sameContact(job.contact, defaultContact);
  });

  return (
    <section className="incoming">
      <div className="incoming-header">
        <button type="button" className="incoming-collapse" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
          <span className="incoming-title">Incoming</span>
          <span className="incoming-count">{shown.length === jobs.length ? jobs.length : `${shown.length} of ${jobs.length}`}</span>
          <span className="incoming-hint">Sent in by customers, waiting for their wheels</span>
          <span className="incoming-toggle">{open ? '▾' : '▸'}</span>
        </button>
        {open && (
          <div className="incoming-filters">
            {defaultContact && (
              <Toggle label="Whose pickups" value={whose} onChange={setWhose}
                      options={[{ value: 'mine', label: `My pickups (${defaultContact})` }, { value: 'all', label: 'Everyone' }]} />
            )}
            <Toggle label="Pickups or drop-offs" value={type} onChange={setType} options={TYPES} />
          </div>
        )}
      </div>
      {error && <div className="incoming-error">{error}</div>}
      {open && !shown.length && !error && (
        <div className="incoming-empty">
          {onlyMine ? `No incoming jobs for ${defaultContact.trim()} right now.` : 'No incoming jobs of this kind.'}
        </div>
      )}
      {open && shown.length > 0 && (
        <ul className="incoming-list">
          {shown.map((job) => (
            <li key={job.id}>
              <div className="incoming-row" role="button" tabIndex={0} onClick={() => onOpen(job)}
                   onKeyDown={(event) => { if (event.key === 'Enter') onOpen(job); }}>
                <div className="incoming-main">
                  <div className="incoming-customer">{job.customer_name}</div>
                  <div className="incoming-details">
                    {[job.make, job.quantity ? `${job.quantity} wheels` : '', job.service, job.ro ? `RO ${job.ro}` : '', job.po ? `PO ${job.po}` : '']
                      .filter(Boolean).join(' · ')}
                  </div>
                  <div className="incoming-meta">
                    <span className={`incoming-intake incoming-intake-${job.intake_method || 'none'}`}>{intakeLabel(job)}</span>
                    {job.contact && <span>{job.contact}</span>}
                    <span>
                      Sent {sentLabel(job.date_created)}
                      {job.portal_submitted_by_name ? ` by ${job.portal_submitted_by_name}` : ''}
                    </span>
                    <span className="incoming-id">#{job.id}</span>
                  </div>
                  {job.customer_notes && <div className="incoming-notes">“{job.customer_notes}”</div>}
                </div>
                <button type="button" className="incoming-receive" disabled={busyId === job.id}
                        onClick={(event) => receive(event, job)}>
                  {busyId === job.id ? 'Saving…' : job.intake_method === 'pickup' ? 'Picked up' : 'Received'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default IncomingJobs;
